import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";
import toast from "react-hot-toast";
import { ArrowLeft, Package, TrendingUp, TrendingDown, FileText, Calendar, Filter, ExternalLink, Pencil, X, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { downloadInventoryHistoryCsv } from "@/utils/inventoryHistoryCsv";

const typeColors = {
  stock_in: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  stock_out: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  invoiced: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
};

const typeIcons = {
  stock_in: TrendingUp,
  stock_out: TrendingDown,
  invoiced: FileText,
};

const emptyItem = { item_name: "", description: "", sku: "", hsn_sac: "", unit: "pcs", price: "", low_stock_threshold: "" };

export default function InventoryHistoryPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [openStockForm, setOpenStockForm] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [stockForm, setStockForm] = useState({ quantity: "" });
  const [form, setForm] = useState(emptyItem);
  const pageSize = 20;

  const { data: historyData = { items: [], total: 0, total_pages: 1 }, isLoading } = useQuery({
    queryKey: ["inventory-history", id, typeFilter, page],
    queryFn: async () => {
      const params = { page, page_size: pageSize };
      if (typeFilter) params.type = typeFilter;
      return (await api.get(`/inventory/${id}/history`, { params })).data;
    },
  });

  const history = historyData.items || [];
  const totalPages = historyData.total_pages || 1;

  const handleExportCsv = async () => {
    try {
      toast.loading("Fetching all records for export...", { id: "export-loading" });
      
      // Fetch all records by paginating through all pages
      const params = {
        page: 1,
        page_size: 100, // Max page size
      };
      if (typeFilter) params.type = typeFilter;
      
      let allHistory = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await api.get(`/inventory/${id}/history`, { params: { ...params, page: currentPage } });
        const entries = response.data?.items || [];
        allHistory = [...allHistory, ...entries];
        
        // Check if there are more pages
        const total = response.data?.total || 0;
        hasMore = allHistory.length < total;
        currentPage++;
        
        // Safety limit to prevent infinite loops
        if (currentPage > 1000) {
          console.warn("Stopping export after 1000 pages");
          break;
        }
      }
      
      const csvData = {
        item: item,
        history: allHistory,
      };
      
      const itemName = item?.item_name?.replace(/[^a-zA-Z0-9]/g, "_") || "inventory_item";
      const filterStr = typeFilter ? `_${typeFilter}` : "";
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `InventoryHistory_${itemName}${filterStr}_${dateStr}.csv`;
      
      await downloadInventoryHistoryCsv(csvData, filename);
      toast.success(`Exported ${allHistory.length} records successfully`, { id: "export-loading" });
    } catch (error) {
      console.error("CSV export failed:", error);
      toast.error("Failed to export CSV", { id: "export-loading" });
    }
  };

  const { data: item } = useQuery({
    queryKey: ["inventory-item", id],
    queryFn: async () => (await api.get(`/inventory/${id}`)).data,
  });

  useEffect(() => {
    if (item) {
      setForm(item);
    }
  }, [item]);

  const save = useMutation({
    mutationFn: async () => {
      return (await api.put(`/inventory/${id}`, form)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory-item", id] });
      toast.success("Item updated");
      setOpenEditForm(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  const stockIn = useMutation({
    mutationFn: async () => {
      const qty = parseFloat(stockForm.quantity);
      if (!qty || qty <= 0) throw new Error("Quantity must be positive");
      return (await api.post(`/inventory/${id}/stock-in`, { quantity: qty })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory-item", id] });
      qc.invalidateQueries({ queryKey: ["inventory-history", id] });
      toast.success("Stock added");
      setOpenStockForm(false);
      setStockForm({ quantity: "" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  const stockOut = useMutation({
    mutationFn: async () => {
      const qty = parseFloat(stockForm.quantity);
      if (!qty || qty <= 0) throw new Error("Quantity must be positive");
      return (await api.post(`/inventory/${id}/stock-out`, { quantity: qty })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory-item", id] });
      qc.invalidateQueries({ queryKey: ["inventory-history", id] });
      toast.success("Stock removed");
      setOpenStockForm(false);
      setStockForm({ quantity: "" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/app/inventory" className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-sm">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="uppercase text-[10px] tracking-[0.2em] text-slate-500 mb-2">Inventory History</div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">
              {item?.item_name || "Item"}
            </h1>
          </div>
        </div>
        <button
          onClick={handleExportCsv}
          className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {item && (
        <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-sm mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
            <div>
              <div className="text-slate-500 text-xs uppercase tracking-wider">SKU</div>
              <div className="font-medium">{item.sku || "—"}</div>
            </div>
            <div>
              <div className="text-slate-500 text-xs uppercase tracking-wider">Current Stock</div>
              <div className={`font-medium ${
                parseFloat(item.quantity) < 0 ? 'text-red-600 dark:text-red-500' :
                parseFloat(item.quantity) <= parseFloat(item.low_stock_threshold) ? 'text-amber-600 dark:text-amber-500' :
                'text-green-600 dark:text-green-500'
              }`}>
                {item.quantity} {item.unit}
              </div>
            </div>
            <div>
              <div className="text-slate-500 text-xs uppercase tracking-wider">Price</div>
              <div className="font-medium">₹{item.price}</div>
            </div>
            <div>
              <div className="text-slate-500 text-xs uppercase tracking-wider">Low Stock Threshold</div>
              <div className="font-medium">{item.low_stock_threshold}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
            <button
              onClick={() => setOpenStockForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73] text-sm"
            >
              <TrendingUp className="w-4 h-4" /> Manage Stock
            </button>
            <button
              onClick={() => setOpenEditForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-sm hover:bg-slate-100 dark:hover:bg-slate-800 text-sm"
            >
              <Pencil className="w-4 h-4" /> Edit
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-slate-500" />
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm"
          >
            <option value="">All Types</option>
            <option value="stock_in">Stock In</option>
            <option value="stock_out">Stock Out</option>
            <option value="invoiced">Invoiced</option>
          </select>
        </div>
        <div className="text-sm text-slate-500">
          {history.length} records
        </div>
      </div>

      <div className="border border-slate-200 dark:border-slate-800 rounded-sm bg-white dark:bg-[#121214] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr className="text-left uppercase text-[10px] tracking-[0.2em] text-slate-500">
              <th className="p-4">Type</th>
              <th className="p-4">Quantity</th>
              <th className="p-4 hidden md:table-cell">Before</th>
              <th className="p-4 hidden md:table-cell">After</th>
              <th className="p-4 hidden lg:table-cell">Reference</th>
              <th className="p-4 hidden lg:table-cell">Date</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!isLoading && history.length === 0 && (
              <tr><td colSpan={6} className="p-12 text-center text-slate-500">
                <Package className="w-6 h-6 mx-auto mb-3 text-slate-400" />
                No history records found.
              </td></tr>
            )}
            {history.map((h) => {
              const Icon = typeIcons[h.transaction_type] || Package;
              return (
                <tr key={h.id} className="border-t border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${
                        h.transaction_type === 'stock_in' ? 'text-green-600' :
                        h.transaction_type === 'stock_out' ? 'text-red-600' :
                        'text-blue-600'
                      }`} />
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[h.transaction_type]}`}>
                        {h.transaction_type.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 font-medium">{h.quantity}</td>
                  <td className="p-4 hidden md:table-cell text-slate-500">{h.quantity_before}</td>
                  <td className="p-4 hidden md:table-cell text-slate-500">{h.quantity_after}</td>
                  <td className="p-4 hidden lg:table-cell text-slate-500">
                    {h.reference_id && h.reference_type === 'invoice' ? (
                      <Link to={`/app/invoices/${h.reference_id}`} className="flex items-center gap-1 hover:text-[#002FA7] hover:underline">
                        <ExternalLink className="w-3 h-3" />
                        Invoice
                      </Link>
                    ) : h.reference_id ? (
                      <span>{h.reference_type} ({h.reference_id.slice(0, 8)}...)</span>
                    ) : (
                      <span>—</span>
                    )}
                  </td>
                  <td className="p-4 hidden lg:table-cell text-slate-500">
                    {new Date(h.created_at).toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-sm disabled:opacity-50 text-sm"
          >
            Previous
          </button>
          <div className="text-sm text-slate-500">
            Page {page} of {totalPages}
          </div>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-sm disabled:opacity-50 text-sm"
          >
            Next
          </button>
        </div>
      )}

      {/* Stock Form Modal */}
      <AnimatePresence>
        {openStockForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setOpenStockForm(false)}
          >
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-md"
            >
              <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
                <h2 className="text-2xl font-extrabold tracking-tighter">Manage Stock</h2>
                <button onClick={() => setOpenStockForm(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6">
                <label className="block mb-4">
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Quantity</span>
                  <input
                    type="number"
                    step="any"
                    value={stockForm.quantity}
                    onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                    placeholder="Enter quantity"
                  />
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => stockIn.mutate()}
                    disabled={!stockForm.quantity || stockIn.isPending}
                    className="flex-1 px-5 py-2.5 bg-green-600 text-white rounded-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    {stockIn.isPending ? "Adding…" : "Stock In"}
                  </button>
                  <button
                    onClick={() => stockOut.mutate()}
                    disabled={!stockForm.quantity || stockOut.isPending}
                    className="flex-1 px-5 py-2.5 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    {stockOut.isPending ? "Removing…" : "Stock Out"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Form Modal */}
      <AnimatePresence>
        {openEditForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setOpenEditForm(false)}
          >
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
                <h2 className="text-2xl font-extrabold tracking-tighter">Edit Item</h2>
                <button onClick={() => setOpenEditForm(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <label>
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Item Name</span>
                  <input
                    value={form.item_name}
                    onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                  />
                </label>
                <label>
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Description</span>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                  />
                </label>
                {[
                  ["sku", "SKU"],
                  ["hsn_sac", "HSN/SAC"],
                  ["unit", "Unit"],
                  ["price", "Price"],
                  ["low_stock_threshold", "Low Stock Threshold"]
                ].map(([k, label]) => (
                  <label key={k}>
                    <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">{label}</span>
                    <input
                      type={k === "price" || k === "low_stock_threshold" ? "number" : "text"}
                      step={k === "price" ? "0.01" : "any"}
                      value={form[k] || ""}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                    />
                  </label>
                ))}
              </div>
              <div className="p-6 flex items-center justify-end gap-3 border-t border-slate-200 dark:border-slate-800">
                <button onClick={() => setOpenEditForm(false)} className="px-5 py-2.5 border border-slate-300 dark:border-slate-700 rounded-sm">Cancel</button>
                <button
                  disabled={!form.item_name || save.isPending}
                  onClick={() => save.mutate()}
                  className="px-5 py-2.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73] disabled:opacity-50"
                >
                  {save.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
